import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import logo from "../assets/images/logo.png";

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeNav, setActiveNav] = useState("home");
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      const scrolled = window.scrollY > 50;
      setIsScrolled(scrolled);
      
      // Update active nav based on scroll position (only for home page sections)
      if (location.pathname === "/") {
        const sections = ['home', 'services', 'about', 'team', 'contact'];
        const currentSection = sections.find(section => {
          const element = document.getElementById(section);
          if (element) {
            const rect = element.getBoundingClientRect();
            return rect.top <= 100 && rect.bottom >= 100;
          }
          return false;
        });
        
        if (currentSection) {
          setActiveNav(currentSection);
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [location.pathname]);

  const goToSection = (id) => {
    setActiveNav(id);
    setIsMobileMenuOpen(false);
    
    if (location.pathname === "/") {
      // Already on home page, scroll to section
      navigate(`#${id}`);
      setTimeout(() => {
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      // On other page, navigate to home with section
      navigate(`/#${id}`);
    }
  };

  const navItems = [
    { id: "services", label: "Services" },
    { id: "about", label: "About" },
    { id: "team", label: "Team" },
    { id: "contact", label: "Contact" },
  ];

  const mobileMenuVariants = {
    closed: {
      opacity: 0,
      height: 0,
      transition: {
        duration: 0.3,
        ease: "easeInOut"
      }
    },
    open: {
      opacity: 1,
      height: "auto",
      transition: {
        duration: 0.4,
        ease: "easeOut"
      }
    }
  };

  const navItemVariants = {
    hover: {
      scale: 1.05,
      transition: {
        duration: 0.2,
        ease: "easeOut"
      }
    },
    tap: {
      scale: 0.95
    }
  };

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled
          ? "bg-white/95 backdrop-blur-xl shadow-2xl border-b border-white/20"
          : "bg-white/80 backdrop-blur-lg"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-3">
          {/* Logo */}
          <motion.div
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="flex-shrink-0"
          >
            <Link to="/" onClick={() => setActiveNav("home")}>
              <img 
                src={logo} 
                alt="SafoLife Technologies" 
                className="w-[180px] h-[40px] transition-all duration-300 hover:brightness-110"
              />
            </Link>
          </motion.div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1">
            {/* Website Themes Link - Navigates to /themes page */}
            <motion.div
              variants={navItemVariants}
              whileHover="hover"
              whileTap="tap"
            >
              <Link to="/themes" onClick={() => {
                setActiveNav("themes");
                setIsMobileMenuOpen(false);
              }}>
                <button className={`relative px-6 py-3 rounded-full font-medium transition-all duration-300 ${
                  location.pathname === "/themes"
                    ? "text-blue-600 bg-blue-50/80"
                    : "text-gray-700 hover:text-blue-600 hover:bg-gray-50/80"
                }`}>
                  {location.pathname === "/themes" && (
                    <motion.div
                      layoutId="activeNav"
                      className="absolute inset-0 bg-gradient-to-r from-blue-500/10 to-indigo-600/10 rounded-full border border-blue-200/50"
                      transition={{ type: "spring", stiffness: 300, damping: 30 }}
                    />
                  )}
                  <span className="relative z-10">Website Themes</span>
                </button>
              </Link>
            </motion.div>

            {/* Home Page Sections */}
            {navItems.map(({ id, label }) => (
              <motion.button
                key={id}
                onClick={() => goToSection(id)}
                variants={navItemVariants}
                whileHover="hover"
                whileTap="tap"
                className={`relative px-6 py-3 rounded-full font-medium transition-all duration-300 ${
                  activeNav === id && location.pathname === "/"
                    ? "text-blue-600 bg-blue-50/80"
                    : "text-gray-700 hover:text-blue-600 hover:bg-gray-50/80"
                }`}
              >
                {activeNav === id && location.pathname === "/" && (
                  <motion.div
                    layoutId="activeNav"
                    className="absolute inset-0 bg-gradient-to-r from-blue-500/10 to-indigo-600/10 rounded-full border border-blue-200/50"
                    transition={{ type: "spring", stiffness: 300, damping: 30 }}
                  />
                )}
                <span className="relative z-10">{label}</span>
              </motion.button>
            ))}
          </nav>

          {/* Desktop CTA Buttons */}
          <div className="hidden lg:flex items-center space-x-4">
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Link to="/portfolio">
                <button className="px-6 py-2.5 text-gray-700 font-medium hover:text-blue-600 transition-colors duration-300">
                  Portfolio
                </button>
              </Link>
            </motion.div>
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {/* <Link to="/get-started">
                <button className="px-8 py-2.5 rounded-full bg-gradient-to-r from-blue-600 to-indigo-700 text-white font-semibold shadow-lg hover:shadow-xl hover:from-blue-700 hover:to-indigo-800 transition-all duration-300 transform hover:-translate-y-0.5">
                  Get Started
                </button>
              </Link> */}
            </motion.div>
          </div>

          {/* Mobile Menu Button */}
          <motion.button
            className="lg:hidden p-3 rounded-2xl bg-white/50 backdrop-blur-sm border border-gray-200/50 shadow-sm"
            whileHover={{ scale: 1.05, backgroundColor: "rgba(255,255,255,0.8)" }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            <div className="w-6 h-6 flex flex-col justify-center space-y-1.5">
              <motion.span
                animate={isMobileMenuOpen ? { rotate: 45, y: 8 } : { rotate: 0, y: 0 }}
                className="block h-0.5 w-6 bg-gray-700 transition-all duration-300"
              />
              <motion.span
                animate={isMobileMenuOpen ? { opacity: 0 } : { opacity: 1 }}
                className="block h-0.5 w-6 bg-gray-700 transition-all duration-300"
              />
              <motion.span
                animate={isMobileMenuOpen ? { rotate: -45, y: -8 } : { rotate: 0, y: 0 }}
                className="block h-0.5 w-6 bg-gray-700 transition-all duration-300"
              />
            </div>
          </motion.button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            variants={mobileMenuVariants}
            initial="closed"
            animate="open"
            exit="closed"
            className="lg:hidden bg-white/95 backdrop-blur-xl border-t border-white/20 shadow-2xl overflow-hidden"
          >
            <div className="px-4 py-6 space-y-2">
              {/* Mobile Website Themes Link */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0 }}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <Link to="/themes" onClick={() => setActiveNav("themes")}>
                  <button className={`w-full text-left px-6 py-4 rounded-2xl font-medium transition-all duration-300 ${
                    location.pathname === "/themes"
                      ? "bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-lg"
                      : "text-gray-700 hover:bg-gray-50/80 hover:text-blue-600"
                  }`}>
                    <div className="flex items-center justify-between">
                      <span>Website Themes</span>
                      {location.pathname === "/themes" && (
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          className="w-2 h-2 bg-white rounded-full"
                        />
                      )}
                    </div>
                  </button>
                </Link>
              </motion.div>

              {/* Mobile Home Page Sections */}
              {navItems.map(({ id, label }, index) => (
                <motion.button
                  key={id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: (index + 1) * 0.1 }}
                  onClick={() => goToSection(id)}
                  className={`w-full text-left px-6 py-4 rounded-2xl font-medium transition-all duration-300 ${
                    activeNav === id && location.pathname === "/"
                      ? "bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-lg"
                      : "text-gray-700 hover:bg-gray-50/80 hover:text-blue-600"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span>{label}</span>
                    {activeNav === id && location.pathname === "/" && (
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        className="w-2 h-2 bg-white rounded-full"
                      />
                    )}
                  </div>
                </motion.button>
              ))}
              
              {/* Mobile CTA Buttons */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                className="pt-4 space-y-3 border-t border-gray-200/50 mt-4"
              >
                <Link to="/portfolio" onClick={() => setIsMobileMenuOpen(false)}>
                  <button className="w-full px-6 py-4 text-gray-700 font-medium hover:text-blue-600 transition-colors duration-300 rounded-2xl hover:bg-gray-50/80">
                    Portfolio
                  </button>
                </Link>
                {/* <Link to="/get-started" onClick={() => setIsMobileMenuOpen(false)}>
                  <button className="w-full px-6 py-4 rounded-2xl bg-gradient-to-r from-blue-600 to-indigo-700 text-white font-semibold shadow-lg hover:shadow-xl hover:from-blue-700 hover:to-indigo-800 transition-all duration-300">
                    Get Started Free
                  </button>
                </Link> */}
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Progress Bar - Only show on home page */}
      {location.pathname === "/" && (
        <motion.div
          className="absolute bottom-0 left-0 h-0.5 bg-gradient-to-r from-blue-500 to-indigo-600"
          style={{
            width: `${((navItems.findIndex(item => item.id === activeNav) + 1) / navItems.length) * 100}%`
          }}
          transition={{ duration: 0.3, ease: "easeOut" }}
        />
      )}
    </motion.header>
  );
};

export default Header;